import { CartAbandonedResponse } from '@/@types/Sells/cart-abandoned';
import type {
  AnalyticsCheckoutsResponse,
  AnalyticsFunnelsResponse,
  AnalyticsOverviewResponse,
  AnalyticsSalesChartsResponse,
} from '@/@types/Analytics';
import {
  AffiliateReportListResponse,
  AffiliateReportRankingsResponse,
  AffiliateReportSummaryResponse,
} from '@/@types/GlobalAffiliateReportResponse';
import {
  addArrayFiltersToParams,
  addStringFiltersToParams,
  cleanFilterParams,
} from '@/utils/convertFilters';
import type { SellsFilterParams } from './sells.service';
import { api } from './api';

async function getReports({
  startDate,
  endDate,
  dateAxis,
  filterParams,
  q,
  currency,
}: {
  startDate: string;
  endDate: string;
  dateAxis?: 'processing' | 'paid' | 'refunded';
  filterParams?: SellsFilterParams;
  q?: string;
  currency: string;
}) {
  const urlParams = new URLSearchParams();

  if (dateAxis === 'paid') {
    urlParams.append('paid_start_date', startDate);
    urlParams.append('paid_end_date', endDate);
  } else if (dateAxis === 'refunded') {
    urlParams.append('refunded_start_date', startDate);
    urlParams.append('refunded_end_date', endDate);
  } else {
    urlParams.append('start_date', startDate);
    urlParams.append('end_date', endDate);
  }
  urlParams.append('currency', currency || 'BRL');

  const cleanedFilterParams = cleanFilterParams(filterParams);
  addArrayFiltersToParams(urlParams, cleanedFilterParams);
  addStringFiltersToParams(urlParams, cleanedFilterParams);

  if (cleanedFilterParams?.invoice_approved === true) {
    urlParams.append('invoice_approved', '1');
  }
  if (cleanedFilterParams?.invoice_not_approved === true) {
    urlParams.append('invoice_not_approved', '1');
  }

  if (q && q.length > 1) {
    urlParams.append('q', q);
  }

  const response = await api.get(
    `/v1/reports/sales/time-distribution?${urlParams.toString()}`,
  );

  return response.data;
}

async function getMetrics({
  startDate,
  endDate,
  currency,
}: {
  startDate: string;
  endDate: string;
  currency: string;
}) {
  const response = await api.get(
    `/v1/reports/sales/metrics?start_date=${startDate}&end_date=${endDate}&currency=${currency}`,
  );

  return response.data;
}

async function getReportsSalesByStatus({
  startDate,
  endDate,
  dateAxis,
  filterParams,
  q,
  currency,
}: {
  startDate: string;
  endDate: string;
  dateAxis?: 'processing' | 'paid' | 'refunded';
  filterParams?: {
    status?: string[];
    payment_methods?: string[];
    currency?: string;
    invoice_approved?: boolean;
    invoice_not_approved?: boolean;
  };
  q?: string;
  currency?: string;
}) {
  const urlParams = new URLSearchParams();

  if (dateAxis === 'paid') {
    urlParams.append('paid_start_date', startDate);
    urlParams.append('paid_end_date', endDate);
  } else if (dateAxis === 'refunded') {
    urlParams.append('refunded_start_date', startDate);
    urlParams.append('refunded_end_date', endDate);
  } else {
    urlParams.append('start_date', startDate);
    urlParams.append('end_date', endDate);
  }
  urlParams.append('currency', currency || filterParams?.currency || 'BRL');

  const cleanedFilterParams = cleanFilterParams(filterParams);
  addArrayFiltersToParams(urlParams, cleanedFilterParams);
  addStringFiltersToParams(urlParams, cleanedFilterParams);

  if (cleanedFilterParams?.invoice_approved === true) {
    urlParams.append('invoice_approved', '1');
  }
  if (cleanedFilterParams?.invoice_not_approved === true) {
    urlParams.append('invoice_not_approved', '1');
  }

  if (q && q.length > 1) {
    urlParams.append('q', q);
  }

  const response = await api.get(
    `/v1/reports/sales/by-status?${urlParams.toString()}`,
  );

  return response.data;
}

async function getReportsCartAbandoned({
  startDate,
  endDate,
  filterParams,
  page,
}: {
  startDate: string;
  endDate: string;
  filterParams?: { products?: string[]; checkouts?: string[]; templates?: string[] };
  page: number;
}) {
  const params = new URLSearchParams();

  params.append('start_date', startDate);
  params.append('end_date', endDate);
  params.append('page', String(page));

  if (filterParams) {
    if (filterParams.products && filterParams.products.length > 0) {
      params.append('products', filterParams.products.join(','));
    }
    if (filterParams.checkouts && filterParams.checkouts.length > 0) {
      params.append('checkouts', filterParams.checkouts.join(','));
    }
    if (filterParams.templates && filterParams.templates.length > 0) {
      params.append('templates', filterParams.templates.join(','));
    }
  }

  const response = await api.get<CartAbandonedResponse>(
    `/v1/sells/abandoned?${params.toString()}`,
  );

  return response.data;
}

export interface AnalyticsParams {
  start_date: string;
  end_date: string;
  checkouts?: string[];
  templates?: string[];
  products?: string[];
  offers?: string[];
  affiliates?: string[];
  coproducers?: string[];
  role?: string;
  currency?: string;
  page?: number;
  per_page?: number;
}

function buildAnalyticsParams({
  checkouts,
  templates,
  products,
  offers,
  affiliates,
  coproducers,
  role,
  start_date,
  end_date,
  currency,
  page,
  per_page,
}: AnalyticsParams): URLSearchParams {
  const urlParams = new URLSearchParams();
  urlParams.append('start_date', start_date);
  urlParams.append('end_date', end_date);
  
  if (currency) {
    urlParams.append('currency', currency);
  }

  if (role) {
    urlParams.append('role', role);
  }

  if (page !== undefined) {
    urlParams.append('page', String(page));
  }

  if (per_page !== undefined) {
    urlParams.append('per_page', String(per_page));
  }

  addArrayFiltersToParams(urlParams, {
    checkouts,
    templates,
    products,
    offers,
    affiliates,
    coproducers,
  });

  return urlParams;
}

async function getAnalyticsOverview(
  params: AnalyticsParams,
): Promise<AnalyticsOverviewResponse> {
  const urlParams = buildAnalyticsParams(params);
  const response = await api.get<AnalyticsOverviewResponse | { data: AnalyticsOverviewResponse }>(
    `/v1/reports/analytics/overview?${urlParams.toString()}`,
  );
  const payload = response.data;

  if (payload && typeof payload === 'object' && 'data' in payload && payload.data) {
    return payload.data;
  }

  return payload as AnalyticsOverviewResponse;
}

async function getAnalyticsFunnels(
  params: AnalyticsParams,
): Promise<AnalyticsFunnelsResponse> {
  const urlParams = buildAnalyticsParams(params);
  const response = await api.get<
    AnalyticsFunnelsResponse | { data: AnalyticsFunnelsResponse }
  >(`/v1/reports/analytics/funnels?${urlParams.toString()}`);
  const payload = response.data;

  if (payload && typeof payload === 'object' && 'data' in payload && payload.data) {
    return payload.data;
  }

  return payload as AnalyticsFunnelsResponse;
}

async function getAnalyticsSalesCharts(
  params: AnalyticsParams,
): Promise<AnalyticsSalesChartsResponse> {
  const urlParams = buildAnalyticsParams(params);
  const response = await api.get<
    AnalyticsSalesChartsResponse | { data: AnalyticsSalesChartsResponse }
  >(`/v1/reports/analytics/charts/sales?${urlParams.toString()}`);
  const payload = response.data;

  if (payload && typeof payload === 'object' && 'data' in payload && payload.data) {
    return payload.data;
  }

  return payload as AnalyticsSalesChartsResponse;
}

async function getAnalyticsCheckouts(
  params: AnalyticsParams,
): Promise<AnalyticsCheckoutsResponse> {
  const urlParams = buildAnalyticsParams(params);
  const response = await api.get<AnalyticsCheckoutsResponse>(
    `/v1/reports/analytics/checkouts?${urlParams.toString()}`,
  );
  return response.data;
}

async function getSalesByCountry({
  start_date,
  end_date,
  currency,
}: {
  start_date: string;
  end_date: string;
  currency?: string;
}) {
  const urlParams = new URLSearchParams();
  urlParams.append('start_date', start_date);
  urlParams.append('end_date', end_date);

  if (currency) {
    urlParams.append('currency', currency);
  }

  const response = await api.get(
    `/v1/reports/sales/by-country?${urlParams.toString()}`,
  );

  return response.data;
}

async function getUtmPanel({
  from,
  to,
  products,
  offers,
  affiliates,
  coproducers,
  checkouts,
  templates,
  searchParam,
  searchValue,
  filters,
  entriesPage,
  entriesPerPage,
  currency = 'BRL',
}: {
  from: string;
  to: string;
  products?: string[];
  offers?: string[];
  affiliates?: string[];
  coproducers?: string[];
  checkouts?: string[];
  templates?: string[];
  searchParam?: string;
  searchValue?: string;
  filters?: Record<string, string[]>;
  entriesPage?: number;
  entriesPerPage?: number;
  currency?: string;
}) {
  const params = new URLSearchParams();

  params.append('from', from);
  params.append('to', to);
  params.append('currency', currency);

  if (products && products.length > 0) {
    products.forEach((p) => params.append('products[]', p));
  }

  if (offers && offers.length > 0) {
    offers.forEach((o) => params.append('offers[]', o));
  }

  if (affiliates && affiliates.length > 0) {
    affiliates.forEach((a) => params.append('affiliates[]', a));
  }

  if (coproducers && coproducers.length > 0) {
    coproducers.forEach((c) => params.append('coproducers[]', c));
  }

  if (checkouts && checkouts.length > 0) {
    checkouts.forEach((c) => params.append('checkouts[]', c));
  }

  if (templates && templates.length > 0) {
    templates.forEach((c) => params.append('templates[]', c));
  }

  if (searchParam && searchValue) {
    params.append('search_param', searchParam);
    params.append('search_value', searchValue);
  }

  if (filters && Object.keys(filters).length > 0) {
    params.append('filters', JSON.stringify(filters));
  }

  if (entriesPage) {
    params.append('entries_page', String(entriesPage));
  }

  if (entriesPerPage) {
    params.append('entries_per_page', String(entriesPerPage));
  }

  const response = await api.get(`/v1/reports/utm-panel?${params.toString()}`);

  return response.data;
}

interface AffiliateReportBaseParams {
  startDate: string;
  endDate: string;
  currency: string;
  q?: string;
  products?: string[];
  affiliates?: string[];
  offers?: string[];
  checkouts?: string[];
  templates?: string[];
}

function buildAffiliateReportParams(params: AffiliateReportBaseParams): URLSearchParams {
  const urlParams = new URLSearchParams();
  urlParams.append('paid_start_date', params.startDate);
  urlParams.append('paid_end_date', params.endDate);
  urlParams.append('currency', params.currency);
  if (params.q && params.q.length > 1) urlParams.append('q', params.q);
  params.products?.forEach((id) => urlParams.append('products[]', id));
  params.affiliates?.forEach((id) => urlParams.append('affiliates[]', id));
  params.offers?.forEach((id) => urlParams.append('offers[]', id));
  params.checkouts?.forEach((id) => urlParams.append('checkouts[]', id));
  params.templates?.forEach((id) => urlParams.append('templates[]', id));
  return urlParams;
}

async function getAffiliateReportList(
  params: AffiliateReportBaseParams & { page: number; perPage?: number; sortBy?: string },
): Promise<AffiliateReportListResponse> {
  const urlParams = buildAffiliateReportParams(params);
  urlParams.append('page', String(params.page));
  if (params.perPage) urlParams.append('per_page', String(params.perPage));
  if (params.sortBy) urlParams.append('sort_by', params.sortBy);

  const response = await api.get<AffiliateReportListResponse>(
    `/v1/reports/affiliates?${urlParams.toString()}`,
  );
  return response.data;
}

async function getAffiliateReportSummary(
  params: AffiliateReportBaseParams,
): Promise<AffiliateReportSummaryResponse> {
  const response = await api.get<AffiliateReportSummaryResponse>(
    `/v1/reports/affiliates/summary?${buildAffiliateReportParams(params).toString()}`,
  );
  return response.data;
}

async function getAffiliateReportRankings(
  params: AffiliateReportBaseParams,
): Promise<AffiliateReportRankingsResponse> {
  const response = await api.get<AffiliateReportRankingsResponse>(
    `/v1/reports/affiliates/rankings?${buildAffiliateReportParams(params).toString()}`,
  );
  return response.data;
}

export const ReportsService = {
  getReports,
  getReportsSalesByStatus,
  getMetrics,
  getReportsCartAbandoned,
  getAnalyticsOverview,
  getAnalyticsFunnels,
  getAnalyticsSalesCharts,
  getAnalyticsCheckouts,
  getSalesByCountry,
  getUtmPanel,
  getAffiliateReportList,
  getAffiliateReportSummary,
  getAffiliateReportRankings,
};
